import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import { formatDistanceToNowStrict } from "date-fns";
import routeNames from "../utils/routes.json";
import moment from "moment";
import { Player } from "@lottiefiles/react-lottie-player";

import {
  useGetSalesQuery,
  useGetActiveSellerProductsQuery,
  useAddProductToSalesMutation, 
} from "../redux/apislice"; 

const Sales = () => {
  const navigate = useNavigate();
  let limit = 10;
  const [page, Setpage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [activeTab, setActiveTab] = useState(null);

  const { data, isLoading, isError } = useGetSalesQuery({ page, limit });
  const {
    data: activeProductData,
    isLoading: isProductLoading,
    isError: isProductError,
    isFetching: isProductFetching,
  } = useGetActiveSellerProductsQuery(page, limit);
  
  const [addProductToSales, { isLoading: isAdding }] = useAddProductToSalesMutation(); 

  const sales = data?.data || [];
  const activeProducts = activeProductData?.data || [];
  const totalPages = data?.totalPages || 1;

  const handleAddProduct = (saleId) => {
    setSelectedSaleId(saleId);
    setShowModal(true);
  };

  const handleSubmitProduct = async () => {
    if (!selectedProductId || !selectedSaleId) return;

    try {
      const body = {
        productId: selectedProductId,
        salesId: selectedSaleId,
      };
      await addProductToSales(body).unwrap();
    } catch (err) {
      console.error("Error adding product to sale:", err);
    }

    setShowModal(false);
    setSelectedProductId("");
  };


  const handleLoadMore = (e) => {
    e.preventDefault();
    Setpage(page + 1);
  };

  const handlePreviousPage = (e) => {
    e.preventDefault();
    if (page > 1) Setpage(page - 1);
  };

  const formatTimer = (timer) => {
    const time = new Date(timer);
    return formatDistanceToNowStrict(time, { addSuffix: true });
  };

  if (isProductLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
      }}>
        <Player
          autoplay
          loop
          src="https://assets1.lottiefiles.com/packages/lf20_raiw2hpe.json"
          style={{ height: '150px', width: '150px' }}
        />
      </div>
    );
  }
  if (isProductError) return <div className="p-6 text-red-500">Failed to fetch products.</div>;

  return (
    <div className="p-6 w-full">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Upcoming Sales</h2>
      
      {sales.length === 0 ? (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6 text-center">
          <h3 className="text-xl font-semibold mb-2">No Active Sales Right Now</h3>
          <p className="text-gray-600 mb-4">
            When any sales become active (like Summer Sale, Winter Clearance, or Holiday Specials),
            you'll be our priority to get notified first!
          </p>
          <p className="text-sm text-gray-500">
            Stay tuned for exciting discounts and promotions coming soon!
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {sales.map((sale) => (
              <div
                key={sale.id}
                className="relative bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      sale.status === "ACTIVE" 
                        ? "bg-green-100 text-green-800" 
                        : sale.status === "UPCOMMING" 
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      {sale.status === "ACTIVE" && (
                        <span className="flex h-2 w-2 relative mr-1">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      )}
                      {sale.status}
                    </span>
                    
                    <div className="text-xs text-gray-500">
                      {formatDistanceToNowStrict(new Date(sale.timer))}
                    </div>
                  </div>
                  
                  <Link
                    to={`/sale-s${routeNames["onsales-products"]}/${sale.id}`}
                    className="block"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors h-14 overflow-hidden">
                      {sale.salesName}
                    </h3>
                  </Link>
                  
                  <div className="text-sm text-gray-600 mb-4">
                    {moment(sale.timer).format("MMM DD, YYYY • hh:mm A")}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Sale ID: #{sale.id}
                    </div>
                    
                    {sale.status === "UPCOMMING" && (
                      <button
                        onClick={() => handleAddProduct(sale.id)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        Add Product
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-purple-500"></div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={() => Setpage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed 
                  ${page === 1 ? "bg-gray-100" : "bg-white hover:bg-gray-50"}`}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              
              <button
                onClick={() => Setpage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed 
                  ${page === totalPages ? "bg-gray-100" : "bg-white hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <Dialog open={showModal} onClose={() => setShowModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-bold mb-4">Select Product to Add</Dialog.Title>
            <select
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(Number(e.target.value))}
            >
              <option value="">-- Select Product --</option>
              {activeProducts.map((prod) => (
                <option key={prod.id} value={prod.id}>
                  {prod.title}
                </option>
              ))}
            </select>

            <div className="flex items-center justify-between mt-2">
              {page > 1 && (
                <button
                  onClick={handlePreviousPage}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  ← Previous
                </button>
              )}

              {activeProductData?.isNext && (
                <button
                  onClick={handleLoadMore}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {isProductFetching ? "Loading..." : "Load More Products"}
                </button>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowModal(false)} 
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitProduct} 
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
              >
                {isAdding ? "Adding..." : "Add Product"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default Sales;